import { useEffect, React, useState, useCallback } from "react";
import { useAuthContext } from "../hooks/useContext/useAuthContext";
import DashboardNavBar from "../components/NavBar/DashboardNavBar";
import Footer from "../components/Shared/Footer";
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/system';
// import { Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { useAllOrdersContext } from "../hooks/useContext/useAllOrdersContext";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from '../hooks/useTranslation';

const GridContainerMain = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.background,
  minHeight: "100vh",
  paddingLeft: "80px",
  paddingRight: "80px",
  paddingTop: "70px",
  marginRight: 'auto',
  paddingBottom: "50px",
  "@media (max-width: 960px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
    paddingTop: "35px",
    marginRight: 'auto',
  },
}));


const AllOrders = ({socket}) => {
  const { user } = useAuthContext();
  const [rows, setRows] = useState([]);
  const { allOrders, allOrdersDispatch } = useAllOrdersContext();
  const theme = useTheme();
  const { t, language } = useTranslation();

  const translateSize = useCallback((size) => {
    if (size === 'Small') return t('small');
    if (size === 'Large') return t('large');
    if (size === 'One Size') return t('oneSize');
    return size;
  }, [t]);

  //initial fetch all orders
  useEffect(() => {
    const fetchAllOrders = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/orders", {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        const json = await response.json();
        if (response.ok) {
          console.log(json);
          allOrdersDispatch({ type: "SET_ALL_ORDERS", payload: json });
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };

    if (user) {
      fetchAllOrders();
    }
  }, [allOrdersDispatch, user]);


  //fecth new orders on socket event
  useEffect(() => {
    // console.log('dashboard socket: ' + socket);
    const fetchNewOrders = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/orders/", {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        const json = await response.json();
        if (response.ok) {
          console.log(json);
          allOrdersDispatch({ type: "SET_ALL_ORDERS", payload: json });
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };

    //data is the returning url from server
    // const handleTopicId = (data) => {
    const handleTopicId = () => {
      fetchNewOrders();
      // console.log(`Received order._id: ${data}`);
    };

    socket.on('newOrder', handleTopicId);
    return () => {
      socket.off('newOrder', handleTopicId);
    };
  }, [user, allOrders, allOrdersDispatch, socket]);


  useEffect(() => {
    if (allOrders && Array.isArray(allOrders)) {
      const updatedRows = allOrders.map((item) => ({
        id: item._id,
        orderNumber: Number.isInteger(item?.dailyOrderNumber)
          ? String(item.dailyOrderNumber).padStart(3, '0')
          : String(item._id || '').slice(-6).toUpperCase(),
        orderPrice: item.totalPrice,
        orderItemsDetails: item.orderItems
          .map(
            (oi) => {
              const translatedName = language === 'pl' ? (t(oi.itemName) || oi.itemName) : oi.itemName;
              const sizeText = oi.itemSize !== "One Size" ? ` ${t('size')}: ${translateSize(oi.itemSize)}` : "";
              return `x${oi.itemQuantity} - ${translatedName}${sizeText}`;
            }
          )
          .join(", "),

        orderSpecialInstructions: (item.orderItems || [])
          .filter((oi) => oi.specialInstructions)
          .map((oi) => ({
            itemName: language === 'pl' ? (t(oi.itemName) || oi.itemName) : oi.itemName,
            text: oi.specialInstructions
          })),

        orderItems: item.orderItems.length,
        orderTime: item.createdAt,
        orderStatus: item.status,
        orderPaidStatus: item.paidStatus,
        orderName: item.name,
        orderEmail: item.email,
        orderReview: item?.customerFeedback?.text || '',
      }));
      setRows(updatedRows);
    }
  }, [allOrders, language, t, translateSize]);
  
  // // set up socket rooms for active topics
  // useEffect(() => {
  //   // Function to join a socket room for a given topic ID
  //   const joinSocketRoom = (roomId) => {
  //     socket.emit('joinRoom', roomId);
  //   };
  
  //   // Function to leave a socket room for a given topic ID
  //   const leaveSocketRoom = (roomId) => {
  //     socket.emit('leaveRoom', roomId);
  //   };
  
  //   // Join socket rooms on component mount and when filteredTopics change
  //   filteredTopics &&
  //   filteredTopics.forEach((topic) => {
  //     if (topic.runningScript) {
  //       joinSocketRoom(topic._id);
  //     }
  //   });
  
  //   // Clean up: Leave socket rooms on component unmount
  //   return () => {
  //     // Leave all socket rooms when the component unmounts
  //     filteredTopics &&
  //       filteredTopics.forEach((topic) => {
  //         if (topic.runningScript) {
  //           leaveSocketRoom(topic._id);
  //         }
  //       });
  //   };
  // }, [socket, filteredTopics]);



  const columns = [
    {
      field: 'orderNumber',
      headerName: t('orderNumber'),
      width: 130,
    },
    { 
      field: 'orderTime',
      headerName: t('time'),
      width: 140,
      valueGetter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + date.toLocaleTimeString([], { hourCycle: 'h23', hour: '2-digit', minute:'2-digit'});
      },
    },
    { 
      field: 'orderPrice',
      headerName: t('price'),
      width: 100, 
      renderCell: (params) => (
        <span>
          {`${params.value} ${t('currency')}`}
        </span>
      ),
    },
    { 
      field: 'orderName',
      headerName: t('name'),
      width: 140, 
      renderCell: (params) => (
        <span>
          {params.value}
        </span>
      ),
    },
    { 
      field: 'orderPaidStatus',
      headerName: t('paymentStatus'),
      width: 100, 
      renderCell: (params) => (
        <span>
          {params.value ? t('paid') : t('unpaid')}
        </span>
      ),
    },
    { 
      field: 'orderItems',
      headerName: t('items'),
      width: 100, 
      renderCell: (params) => (
        <span>{`${params.value} ${params.value === 1 ? t('item') : t('items')}`}</span>
      ),
    },
    { 
      field: 'orderItemsDetails',
      headerName: t('details'),
      width: 400,
      renderCell: (params) => (
        <Tooltip title={params.value} placement="top-start" arrow>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: 'orderSpecialInstructions',
      headerName: t('instructions'),
      width: 300,
      renderCell: (params) => {
        const list = params.value;

        if (!Array.isArray(list) || list.length === 0) {
          return <span style={{ fontStyle: "italic", color: "#777" }}>{t('none')}</span>;
        }

        return (
          <Tooltip
            title={list.map(i => `${i.itemName}: ${i.text}`).join(", ")}
            placement="top-start"
            arrow
          >
            <span>
              {list.map((i, idx) => (
                <span key={idx}>
                  {i.itemName}:&nbsp;
                  <i>{i.text}</i>
                  {idx < list.length - 1 ? ", " : ""}
                </span>
              ))}
            </span>
          </Tooltip>
        );
      }
    },
    { 
      field: 'orderStatus',
      headerName: t('status'),
      width: 150, 
      renderCell: (params) => (
        <span>
          {params.value}
        </span>
      ),
    },
    { 
      field: 'orderEmail',
      headerName: t('email'),
      width: 300, 
      renderCell: (params) => (
        <span>
          {params.value}
        </span>
      ),
    },
    {
      field: 'orderReview',
      headerName: t('review'),
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params.value) {
          return <span style={{ fontStyle: "italic", color: "#777" }}>{t('none')}</span>;
        }

        return (
          <Button
            variant="outlined"
            size="small"
            onClick={() => window.alert(params.value)}
            sx={{ textTransform: 'none' }}
          >
            {t('viewReview')}
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{backgroundColor: theme.palette.common.background}}>
      <DashboardNavBar />

      <GridContainerMain>
        <div className="Container">
          <div className="Grid">
            {/* <DataGrid rows={usersArticles ? usersArticles : []} columns={columns} /> */}
            <DataGrid rows={rows} columns={columns} />
          </div>
        </div>
      </GridContainerMain>

      <Footer />
    </div>
  );
};

export default AllOrders;